import { APP_ENV } from '../../config'

import { ProductTypes } from './product-types'

/** Type-safe array inclusion check (avoids unsafe cast on readonly tuple `.includes()`) */
function isInArray<T>(arr: readonly T[], value: unknown): value is T {
  return (arr as readonly unknown[]).includes(value)
}

interface Values {
  // Package Products - simplified pricing (no individual/company distinction)
  [ProductTypes.STANDARD_PACKAGE]: {
    production: {
      llc: string // Single-member LLC
      ccorp: string // C-Corp & Multi-member LLC
    }
    staging: {
      llc: string
      ccorp: string
    }
  }
  [ProductTypes.FULL_SUITE]: {
    production: {
      llc: string // Single-member LLC
      ccorp: string // C-Corp & Multi-member LLC
    }
    staging: {
      llc: string
      ccorp: string
    }
  }

  // Standalone Services
  [ProductTypes.MAILROOM]: {
    production: string
    staging: string
  }
  [ProductTypes.REGISTERED_AGENT]: {
    production: string
    staging: string
  }
  [ProductTypes.ACCOUNTING]: {
    production: string
    staging: string
  }
  [ProductTypes.FEDERAL_TAX]: {
    production: {
      subscription: {
        llc: string // Single-member LLC
        ccorp: string // Multi-member LLC & C-Corp
      }
      single_payment: {
        llc: string
        ccorp: string
      }
    }
    staging: {
      subscription: {
        llc: string
        ccorp: string
      }
      single_payment: {
        llc: string
        ccorp: string
      }
    }
  }
  [ProductTypes.FEDERAL_TAX_LATE]: {
    production: {
      single_payment: {
        llc: string
        ccorp: string
      }
    }
    staging: {
      single_payment: {
        llc: string
        ccorp: string
      }
    }
  }
  [ProductTypes.STATE_TAX]: {
    production: {
      subscription: string
      single_payment: string
    }
    staging: {
      subscription: string
      single_payment: string
    }
  }

  // One-time Fees
  [ProductTypes.STATE_FEE]: {
    production: {
      wyoming_llc: string
      wyoming_ccorp: string
      delaware_llc: string
      delaware_ccorp: string
    }
    staging: {
      wyoming_llc: string
      wyoming_ccorp: string
      delaware_llc: string
      delaware_ccorp: string
    }
  }
}

/**
 * The live $1,499/year Accounting price (owner decision B2, 2026-09-28;
 * created in live mode 2026-09-29). The same constant sits in
 * apps/api/src/payments/entitlements.ts; keep the two in step.
 */
export const LIVE_ACCOUNTING_1499_PRICE_ID = 'price_1UKvX3JPHKeudrr7v7Mew74y'

/**
 * The $1,999/year Accounting prices (live, sandbox) sold before owner decision
 * B2. Stripe keeps existing subscribers on the price they bought, so these must
 * keep granting accounting. Recognition only — never offered for a new checkout.
 */
const RETIRED_ACCOUNTING_1999_PRICE_IDS = ['price_1SugheJPHKeudrr7rWnRv4ey', 'price_1SugiwQwIhclkKIgtN60sLMG'] as const

const values: Values = {
  // Package Products
  [ProductTypes.STANDARD_PACKAGE]: {
    production: {
      llc: 'price_1QCiKQJPHKeudrr7PZLRBtFG', // Keep existing production
      ccorp: 'price_1PrGhCJPHKeudrr7QycmhCDl', // Keep existing production
    },
    staging: {
      llc: 'price_1S2wXTQwIhclkKIgBwlFNGyn', // New sandbox: $199/year
      ccorp: 'price_1S2wXTQwIhclkKIgTHibXWcb', // New sandbox: $299/year
    },
  },
  [ProductTypes.FULL_SUITE]: {
    production: {
      llc: 'price_1PrGimJPHKeudrr7wxI0cLoZ', // Keep existing production
      ccorp: 'price_1PrGimJPHKeudrr7Xtp3vmyy', // Live Full Compliance Suite C-Corp price
    },
    staging: {
      llc: 'price_1S2wyKQwIhclkKIg793PtrAE', // Sandbox: $1999/year — matches production
      ccorp: 'price_1U3fTCQwIhclkKIgs76ZDjFn', // Sandbox: $2499/year — matches production
    },
  },

  // Standalone Services
  [ProductTypes.MAILROOM]: {
    production: 'price_1SGHo9JPHKeudrr7yWO868Yx', // Keep existing
    staging: 'price_1S2uPtQwIhclkKIganRG6BBA', // New sandbox: $149/year
  },
  [ProductTypes.REGISTERED_AGENT]: {
    production: 'price_1Pqhx7JPHKeudrr73DHc1SdV', // Keep existing
    staging: 'price_1SGHwJQwIhclkKIgek4lfMXr', // New sandbox: $100/year
  },
  // Owner decision B2 (2026-09-28): $1,499/year. The $1,999 prices existing
  // subscribers keep are in RETIRED_ACCOUNTING_1999_PRICE_IDS below.
  [ProductTypes.ACCOUNTING]: {
    production: LIVE_ACCOUNTING_1499_PRICE_ID, // $1,499/year
    staging: 'price_1S2wghQwIhclkKIgOQi8BMNl', // Sandbox: $1,499/year
  },
  [ProductTypes.FEDERAL_TAX]: {
    production: {
      subscription: {
        llc: 'price_1SHfWqJPHKeudrr7nK1ugRgU', // Keep existing
        ccorp: 'price_1Pf4DdJPHKeudrr7TpfHDfBI', // Keep existing
      },
      single_payment: {
        llc: 'price_1SHfUdJPHKeudrr7FLbEmWf7', // Keep existing
        ccorp: 'price_1Pf4f1JPHKeudrr7mFT4oP6d', // Keep existing
      },
    },
    staging: {
      subscription: {
        llc: 'price_1S2wmfQwIhclkKIgcbrkZiqT', // New sandbox: $399/year
        ccorp: 'price_1S2wmfQwIhclkKIgy53qdvPk', // New sandbox: $899/year
      },
      single_payment: {
        llc: 'price_1S2wqiQwIhclkKIgahAozvRB', // New sandbox: $399 one-off
        ccorp: 'price_1S2wrZQwIhclkKIgvhACvu9U', // New sandbox: $899 one-off
      },
    },
  },
  [ProductTypes.FEDERAL_TAX_LATE]: {
    production: {
      single_payment: {
        llc: 'price_1TEeQ5JPHKeudrr7nrFr2PEJ', // $750
        ccorp: 'price_1TEeQOJPHKeudrr7784TniQq', // $1,500
      },
    },
    staging: {
      single_payment: {
        llc: 'price_1TEcBAQwIhclkKIgkaeGgOhS', // $750
        ccorp: 'price_1TEcBVQwIhclkKIglviBcBJo', // $1,500
      },
    },
  },
  [ProductTypes.STATE_TAX]: {
    production: {
      subscription: 'price_1QRJUHJPHKeudrr7NSION95K', // Keep existing
      single_payment: 'price_1QRJUcJPHKeudrr7yQL1JkVh', // Keep existing
    },
    staging: {
      subscription: 'price_1S2wukQwIhclkKIggAHg0b9L', // Sandbox: $89/year (not sold; recognition only)
      // The price the API Worker's state-tax checkout charges on staging
      // (apps/api/src/state-tax/pricing.ts): "State Filing Service", $89 one-off.
      single_payment: 'price_1SPgC3QwIhclkKIgVmakgTSd',
    },
  },

  // One-time Fees
  [ProductTypes.STATE_FEE]: {
    production: {
      wyoming_llc: 'price_1PqkSRJPHKeudrr73smwi8rJ', // Keep existing
      wyoming_ccorp: 'price_1PqkSRJPHKeudrr73smwi8rJ', // Same as LLC for now
      delaware_llc: 'price_1PqkSRJPHKeudrr78o53IiP4', // Keep existing
      delaware_ccorp: 'price_1PqkSRJPHKeudrr78o53IiP4', // Same as LLC for now
    },
    staging: {
      wyoming_llc: 'price_1S305RQwIhclkKIgFlfCIISO', // New sandbox: $110
      wyoming_ccorp: 'price_1S305RQwIhclkKIgFlfCIISO', // Same as LLC
      delaware_llc: 'price_1S305RQwIhclkKIgilnfjZPU', // New sandbox: $120
      delaware_ccorp: 'price_1S305RQwIhclkKIgilnfjZPU', // Same as LLC
    },
  },
}

/**
 * Get the price configuration for a product in the current environment.
 */
export function getPriceId(key: ProductTypes.STANDARD_PACKAGE | ProductTypes.FULL_SUITE): { llc: string; ccorp: string }
export function getPriceId(key: ProductTypes.MAILROOM | ProductTypes.REGISTERED_AGENT | ProductTypes.ACCOUNTING): string
export function getPriceId(key: ProductTypes.FEDERAL_TAX): {
  subscription: { llc: string; ccorp: string }
  single_payment: { llc: string; ccorp: string }
}
export function getPriceId(key: ProductTypes.FEDERAL_TAX_LATE): {
  single_payment: { llc: string; ccorp: string }
}
export function getPriceId(key: ProductTypes.STATE_TAX): { subscription: string; single_payment: string }
export function getPriceId(key: ProductTypes.STATE_FEE): {
  wyoming_llc: string
  wyoming_ccorp: string
  delaware_llc: string
  delaware_ccorp: string
}
// Generic overload for callers iterating over all ProductTypes at runtime
export function getPriceId(key: ProductTypes): unknown
export function getPriceId(key: ProductTypes): unknown {
  // APP_ENV() only ever returns 'production' | 'staging' in real environments.
  // Be defensive against any other value (e.g. a test that mocks APP_ENV to
  // 'test') so the price registry — and the module-load-time PRICE_IDS
  // constants derived from it — never crash on `undefined[...]`. Mirrors
  // APP_ENV's own non-staging → production default.
  const env = APP_ENV() === 'staging' ? 'staging' : 'production'
  return values[key][env]
}

// Helper functions for common price lookups
export const getPackagePriceId = (
  packageType: 'standard' | 'full',
  companyType: 'LLC' | 'CCorp',
  isMultiMember: boolean = false,
): string => {
  const productKey = packageType === 'standard' ? ProductTypes.STANDARD_PACKAGE : ProductTypes.FULL_SUITE
  const priceKey = companyType === 'LLC' && !isMultiMember ? 'llc' : 'ccorp'
  return getPriceId(productKey)[priceKey]
}

// Export arrays for checking if a price is a package
export const STANDARD_PACKAGE_PRICE_IDS = [
  getPriceId(ProductTypes.STANDARD_PACKAGE).llc,
  getPriceId(ProductTypes.STANDARD_PACKAGE).ccorp,
] as const

/**
 * Staging Full Suite C-Corp before it was realigned to production's $2,499.
 * A Stripe price amount is immutable, so the correction had to be a new price
 * id; sandbox subscriptions already sold on the $3,499 one must keep resolving
 * as Full Suite. Recognition only — never offered for a new checkout, and
 * inert in production because no production subscription can carry it.
 */
const RETIRED_STAGING_FULL_SUITE_CCORP_PRICE_ID = 'price_1S2wyKQwIhclkKIgOZLmtB3r'

export const FULL_SUITE_PRICE_IDS = [
  getPriceId(ProductTypes.FULL_SUITE).llc,
  getPriceId(ProductTypes.FULL_SUITE).ccorp,
  RETIRED_STAGING_FULL_SUITE_CCORP_PRICE_ID,
] as const

export const ALL_PACKAGE_PRICE_IDS = [...STANDARD_PACKAGE_PRICE_IDS, ...FULL_SUITE_PRICE_IDS] as const

// Standalone service price IDs for subscription selection
export const STANDALONE_MAILROOM_PRICE_ID = getPriceId(ProductTypes.MAILROOM)

/**
 * Every standalone Accounting price a subscription can carry, in either
 * environment: the $1,499 prices new checkouts use and the retired $1,999 ones.
 */
const ALL_ACCOUNTING_PRICE_IDS: readonly string[] = [
  values[ProductTypes.ACCOUNTING].production,
  values[ProductTypes.ACCOUNTING].staging,
  ...RETIRED_ACCOUNTING_1999_PRICE_IDS,
]

// Federal and state tax prices nest their recurring ids (per company type);
// a subscriber on any of them, in either environment, holds the service.
const FEDERAL_TAX_SUBSCRIPTION_PRICE_IDS: readonly string[] = [
  ...Object.values(values[ProductTypes.FEDERAL_TAX].production.subscription),
  ...Object.values(values[ProductTypes.FEDERAL_TAX].staging.subscription),
]
const STATE_TAX_SUBSCRIPTION_PRICE_IDS: readonly string[] = [
  values[ProductTypes.STATE_TAX].production.subscription,
  values[ProductTypes.STATE_TAX].staging.subscription,
]

function standalonePriceIds(serviceType: ProductTypes): readonly string[] {
  if (serviceType === ProductTypes.ACCOUNTING) return ALL_ACCOUNTING_PRICE_IDS
  if (serviceType === ProductTypes.FEDERAL_TAX) return FEDERAL_TAX_SUBSCRIPTION_PRICE_IDS
  if (serviceType === ProductTypes.STATE_TAX) return STATE_TAX_SUBSCRIPTION_PRICE_IDS
  const priceId = getPriceId(serviceType)
  return typeof priceId === 'string' ? [priceId] : []
}

/**
 * Services included in each package type
 */
const PACKAGE_INCLUDED_SERVICES = {
  standard: [ProductTypes.MAILROOM, ProductTypes.REGISTERED_AGENT],
  full_suite: [
    ProductTypes.MAILROOM,
    ProductTypes.REGISTERED_AGENT,
    ProductTypes.FEDERAL_TAX,
    ProductTypes.STATE_TAX,
    ProductTypes.ACCOUNTING,
  ],
} as const

/**
 * Check if a service is included in a package subscription
 * @param priceId - The subscription price ID to check
 * @param serviceType - The service type to check for inclusion
 * @returns true if the service is included in the package
 */
function _isServiceIncludedInPackage(priceId: string, serviceType: ProductTypes): boolean {
  // Check Full Suite first (includes more services)
  if (isInArray(FULL_SUITE_PRICE_IDS, priceId)) {
    return isInArray(PACKAGE_INCLUDED_SERVICES.full_suite, serviceType)
  }

  // Check Standard Package
  if (isInArray(STANDARD_PACKAGE_PRICE_IDS, priceId)) {
    return isInArray(PACKAGE_INCLUDED_SERVICES.standard, serviceType)
  }

  return false
}

/**
 * All price IDs that include mailroom service (standalone + packages)
 */
export const ALL_MAILROOM_PRICE_IDS = [
  STANDALONE_MAILROOM_PRICE_ID,
  ...STANDARD_PACKAGE_PRICE_IDS,
  ...FULL_SUITE_PRICE_IDS,
] as const

/**
 * Determine the subscription source label from a price ID
 */
export function getSubscriptionSource(priceId: string): 'Standalone' | 'Standard' | 'Full Suite' {
  if (isInArray(FULL_SUITE_PRICE_IDS, priceId)) return 'Full Suite'
  if (isInArray(STANDARD_PACKAGE_PRICE_IDS, priceId)) return 'Standard'
  return 'Standalone'
}

/**
 * Source specificity ranking (higher is more specific).
 * Used when multiple items map to the same subscription/company.
 */
export const SUBSCRIPTION_SOURCE_SPECIFICITY = {
  Standalone: 2,
  Standard: 1,
  'Full Suite': 0,
} as const

export function getSubscriptionSourceSpecificity(source: keyof typeof SUBSCRIPTION_SOURCE_SPECIFICITY): number {
  return SUBSCRIPTION_SOURCE_SPECIFICITY[source]
}

/**
 * Check if user has entitlement to a service based on their subscriptions
 * @param subscriptionPriceIds - Array of active subscription price IDs
 * @param serviceType - The service type to check entitlement for
 * @returns Object with entitlement info
 */
export function checkServiceEntitlement(
  subscriptionPriceIds: string[],
  serviceType: ProductTypes,
): {
  hasAccess: boolean
  source: 'package' | 'standalone' | 'none'
  packageType?: 'standard' | 'full_suite'
} {
  // Check for Full Suite package first (highest tier)
  const hasFullSuite = subscriptionPriceIds.some((id) => isInArray(FULL_SUITE_PRICE_IDS, id))
  if (hasFullSuite && isInArray(PACKAGE_INCLUDED_SERVICES.full_suite, serviceType)) {
    return { hasAccess: true, source: 'package', packageType: 'full_suite' }
  }

  // Check for Standard Package
  const hasStandardPackage = subscriptionPriceIds.some((id) => isInArray(STANDARD_PACKAGE_PRICE_IDS, id))
  if (hasStandardPackage && isInArray(PACKAGE_INCLUDED_SERVICES.standard, serviceType)) {
    return { hasAccess: true, source: 'package', packageType: 'standard' }
  }

  // Check for standalone subscription. Accounting has two price generations
  // (owner decision B2), and a subscriber on either keeps access.
  const standalonePrices = standalonePriceIds(serviceType)
  const hasStandalone = subscriptionPriceIds.some((id) => standalonePrices.includes(id))
  if (hasStandalone) {
    return { hasAccess: true, source: 'standalone' }
  }

  return { hasAccess: false, source: 'none' }
}
